import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { type ReactNode, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { PersistGate } from 'redux-persist/integration/react';

import { persistor, store } from '@/app/providers/store';
import { RemoteConfigProvider } from '@/contexts/RemoteConfig/RemoteConfig';
import { CmsProvider } from '@/features/chat/contexts/CmsContext';
import { MessageDraftProvider } from '@/features/chat/contexts/MessageDraftContext';
import { ExperimentProvider } from '@/features/experiment/contexts/Experiment';
import { loadToastifyStyles } from '@/uikit/components/Toast/loadToastifyStyles';
import { loadOptionalUiStyles } from '@/uikit/styles/loadOptionalUiStyles';

import { gb } from './providers/growthbook';
import './providers/i18n';

interface AppProviderProps {
  children: React.ReactNode;
}

export function AppProvider({ children }: AppProviderProps): ReactNode {
  useEffect(() => {
    const loadStyles = (): void => {
      void loadToastifyStyles();
      void loadOptionalUiStyles();
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(loadStyles, { timeout: 2000 });

      return () => {
        window.cancelIdleCallback(idleId);
      };
    }

    loadStyles();

    return undefined;
  }, []);

  return (
    <GrowthBookProvider growthbook={gb}>
      <ExperimentProvider>
        <RemoteConfigProvider>
          <HelmetProvider>
            <ReduxProvider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <CmsProvider>
                  <MessageDraftProvider>
                    {children}
                    <ToastContainer hideProgressBar />
                  </MessageDraftProvider>
                </CmsProvider>
              </PersistGate>
            </ReduxProvider>
          </HelmetProvider>
        </RemoteConfigProvider>
      </ExperimentProvider>
    </GrowthBookProvider>
  );
}
